import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { adminListMessages, adminSetMessageStatus, apiError } from "@/lib/blogApi";

const MSG_STATUS_LABEL = { unread: "Unread", in_progress: "In Progress", resolved: "Resolved" };
const MSG_STATUS_COLOR = {
  unread: "text-amber-400 border-amber-600",
  in_progress: "text-sky-400 border-sky-600",
  resolved: "text-[#00C5FE] border-[#00C5FE]/50",
};

const replyHref = (type, item) => {
  const to = type === "contact" ? item.email : item.reporter_email;
  if (!to) return null;
  const subject =
    type === "contact"
      ? `Re: ${item.subject || "Your message to LinkExpln"}`
      : `Your LinkExpln link report (${item.url || ""})`;
  return `mailto:${to}?subject=${encodeURIComponent(subject)}`;
};

export const AdminMessages = () => {
  const [type, setType] = useState("contact");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    adminListMessages(type, status).then((d) => setItems(d.items || [])).catch((e) => toast.error(apiError(e)));
  }, [type, status]);
  useEffect(() => { load(); }, [load]);

  const setItemStatus = async (id, s) => {
    try {
      await adminSetMessageStatus(type, id, s);
      toast.success(`Marked ${MSG_STATUS_LABEL[s]}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-messages-panel">
      <div className="mt-6 flex flex-wrap gap-2">
        {[["contact", "Contact Messages"], ["report", "Link Reports"]].map(([t, label]) => (
          <button key={t} data-testid={`messages-type-${t}`} onClick={() => { setType(t); setOpenId(null); }}
            className={`px-3 py-1.5 font-mono-ls text-[10px] tracking-[0.12em] uppercase border transition-colors duration-200 ${type === t ? "btn-brand text-black border-transparent" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
        <span className="mx-2 border-l border-white/10" />
        {[["", "All"], ["unread", "Unread"], ["in_progress", "In Progress"], ["resolved", "Resolved"]].map(([s, label]) => (
          <button key={s} data-testid={`messages-status-${s || "all"}`} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 font-mono-ls text-[10px] tracking-[0.12em] uppercase border transition-colors duration-200 ${status === s ? "btn-brand text-black border-transparent" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-px bg-white/10 border border-white/10">
        {items.length === 0 && <p data-testid="messages-empty" className="bg-[#0A121D] p-6 text-zinc-400 text-sm">No submissions in this view.</p>}
        {items.map((m) => (
          <div key={m.id} data-testid={`message-row-${m.id}`} className="bg-[#0A121D] p-5">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[220px]">
                {type === "contact" ? (
                  <>
                    <p className="font-heading font-semibold text-white break-words">{m.subject || "(no subject)"}</p>
                    <p className="font-mono-ls text-[10px] tracking-[0.12em] text-zinc-400 mt-1 break-all">{m.name} · {m.email} · {(m.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </>
                ) : (
                  <>
                    <p className="font-mono-ls text-xs text-white break-all">{m.url}</p>
                    <p className="font-mono-ls text-[10px] tracking-[0.12em] text-zinc-400 mt-1 break-all">{m.category} · {m.reporter_email || "anonymous"} · {(m.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </>
                )}
              </div>
              <span className={`px-2.5 py-1 border font-mono-ls text-[10px] tracking-[0.12em] uppercase ${MSG_STATUS_COLOR[m.status]}`}>{MSG_STATUS_LABEL[m.status]}</span>
              <button data-testid={`message-open-${m.id}`} onClick={() => setOpenId(openId === m.id ? null : m.id)}
                className="text-xs text-zinc-300 hover:text-[#00C5FE] underline underline-offset-4 transition-colors duration-200">
                {openId === m.id ? "Close" : "Open"}
              </button>
            </div>

            {openId === m.id && (
              <div data-testid={`message-detail-${m.id}`} className="mt-4 border-t border-white/10 pt-4">
                {type === "contact" ? (
                  <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words">{m.message}</p>
                ) : (
                  <>
                    <p className="font-mono-ls text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Reported URL (do not open)</p>
                    <code className="block text-xs text-amber-300 break-all bg-black border border-white/10 p-3">{m.url}</code>
                    {m.description && <p className="mt-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words">{m.description}</p>}
                  </>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {replyHref(type, m) && (
                    <a href={replyHref(type, m)} data-testid={`message-reply-${m.id}`}
                      className="inline-flex items-center gap-1.5 border border-[#00C5FE]/40 text-[#00C5FE] font-heading font-semibold text-xs px-3 py-2 hover:bg-[#00C5FE]/10 transition-colors duration-200">
                      <Mail size={13} /> Reply by Email
                    </a>
                  )}
                  {Object.keys(MSG_STATUS_LABEL).filter((s) => s !== m.status).map((s) => (
                    <button key={s} data-testid={`message-set-${s}-${m.id}`} onClick={() => setItemStatus(m.id, s)}
                      className="text-xs text-zinc-300 hover:text-white underline underline-offset-4 transition-colors duration-200">
                      Mark {MSG_STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
